import { Tag, TagAppearance, TagColor, TagSize } from '@/components/Tag'
import { IconName } from '@/components/Icon'

const appearances: TagAppearance[] = ['solid', 'subtle', 'stroke']
const colors: TagColor[] = [
	'brand',
	'content1',
	'content2',
	'content3',
	'success',
	'warning',
	'danger',
	'note',
	'overlay'
]
const sizes: TagSize[] = ['xs', 'sm', 'md']
const sampleIcon: IconName = 'Sparkle'

export function TagSandbox () {
	return (
		<div className='spaces:flex spaces:flex-col spaces:gap-32'>
			{/* ── Size × Appearance matrix ── */}
			<Section title='Sizes × Appearances (brand)'>
				<table className='spaces:border-collapse'>
					<thead>
						<tr>
							<th className='spaces:text-left spaces:text-12 spaces:text-content-3 spaces:pr-16 spaces:pb-8' />
							{sizes.map(s => (
								<th key={s} className='spaces:text-left spaces:text-12 spaces:text-content-3 spaces:px-8 spaces:pb-8'>
									{s}
								</th>
							))}
						</tr>
					</thead>
					<tbody>
						{appearances.map(a => (
							<tr key={a}>
								<td className='spaces:text-12 spaces:text-content-3 spaces:pr-16 spaces:py-8 spaces:align-middle spaces:font-default'>
									{a}
								</td>
								{sizes.map(s => (
									<td key={s} className='spaces:px-8 spaces:py-8 spaces:align-middle'>
										<Tag appearance={a} color='brand' size={s} label='Label' />
									</td>
								))}
							</tr>
						))}
					</tbody>
				</table>
			</Section>

			{/* ── Color × Appearance matrix ── */}
			<Section title='Colors × Appearances (sm)'>
				<div className='spaces:overflow-x-auto'>
					<table className='spaces:border-collapse'>
						<thead>
							<tr>
								<th className='spaces:text-left spaces:text-12 spaces:text-content-3 spaces:pr-16 spaces:pb-8' />
								{appearances.map(a => (
									<th key={a} className='spaces:text-left spaces:text-12 spaces:text-content-3 spaces:px-8 spaces:pb-8'>
										{a}
									</th>
								))}
							</tr>
						</thead>
						<tbody>
							{colors.map(c => (
								<tr key={c} className={c === 'overlay' ? 'spaces:bg-content-1' : ''}>
									<td className='spaces:text-12 spaces:text-content-3 spaces:pr-16 spaces:py-8 spaces:align-middle spaces:font-default'>
										{c}
									</td>
									{appearances.map(a => (
										<td key={a} className='spaces:px-8 spaces:py-8 spaces:align-middle'>
											<Tag appearance={a} color={c} size='sm' label='Label' />
										</td>
									))}
								</tr>
							))}
						</tbody>
					</table>
				</div>
			</Section>

			{/* ── Icon variants ── */}
			<Section title='Icon Variants'>
				<div className='spaces:flex spaces:flex-wrap spaces:items-end spaces:gap-16'>
					{sizes.map(s => (
						<div key={`l-${s}`} className='spaces:flex spaces:flex-col spaces:items-center spaces:gap-4'>
							<span className='spaces:text-10 spaces:text-content-4'>{s} / iconLeft</span>
							<Tag size={s} label='Label' iconLeft={sampleIcon} />
						</div>
					))}
					{sizes.map(s => (
						<div key={`r-${s}`} className='spaces:flex spaces:flex-col spaces:items-center spaces:gap-4'>
							<span className='spaces:text-10 spaces:text-content-4'>{s} / iconRight</span>
							<Tag size={s} label='Label' iconRight={sampleIcon} />
						</div>
					))}
					{sizes.map(s => (
						<div key={`b-${s}`} className='spaces:flex spaces:flex-col spaces:items-center spaces:gap-4'>
							<span className='spaces:text-10 spaces:text-content-4'>{s} / both</span>
							<Tag
								size={s}
								label='Label'
								iconLeft={sampleIcon}
								iconRight={sampleIcon}
							/>
						</div>
					))}
				</div>
			</Section>
		</div>
	)
}

function Section ({
	title,
	children
}: {
	title: string
	children: React.ReactNode
}) {
	return (
		<div className='spaces:flex spaces:flex-col spaces:gap-12'>
			<h3 className='spaces:text-16 spaces:font-semi spaces:text-content-1'>{title}</h3>
			{children}
		</div>
	)
}
